import { useRef, useState } from "react";
import { Button } from "react-aria-components";
import { useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router";
import {
  usePassengerDetailsContext,
  type PassengerDetails,
} from "src/PassengerDetailsProvider";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { SearchButton } from "src/components/SearchButton/SearchButton";
import { SearchInput } from "src/components/SearchInput/SearchInput";
import SwapHorizontal from "src/design-system/icons/SwapHorizontal.svg?react";
import { useReturnFlowState } from "src/domain/SegmentScreen/ReturnFlowStateProvider";
import SearchCombinedPicker from "src/domain/SegmentScreen/SegmentHeader/CombinedPicker/Experiment/SearchCombinedPicker";
import { useTravelSearchSettings } from "src/domain/SegmentScreen/TravelSearchSettingsProvider";
import { localeToLanguageCode } from "src/utils/conversions/languageCode";
import {
  dateFromIsoDateTime,
  formatIsoDate,
  formatIsoDateTime,
  localDateObj,
  parseIsoDate,
  timeFromIsoDateTime,
} from "src/utils/dateTime";
import { getDefaultDateTime } from "src/utils/getDefaultDateTime";
import {
  useSearchInput,
  useSearchKeyboard,
} from "src/utils/hooks/useSearchInput";
import { useTypedLocation } from "src/utils/hooks/useTypedLocation";
import { getPath } from "src/utils/url";
import { getDateParametersString } from "src/utils/hooks/useDateURL";
import { encodeSearchParamsForUrl } from "src/utils/encodeSearchParamsForUrl";
import { FocusContext, type FocusedElement } from "../../../FocusContext";
import type { AutocompletePlace } from "../../../api/v1.6/autocomplete";
import useSearch from "../../../utils/hooks/useSearch";
import messages from "../LargeSearchBar.messages.ts";
import inputMessages from "../SearchInput/SearchInput.messages.ts";

type TravelSettingsState = {
  passengerDetails: PassengerDetails;
  departureDateString: string;
  returnDateString?: string;
};

export function DatesSearchBar() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { origin, destination } = useSearch();
  const keyboard = useSearchKeyboard();
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const typedLocation = useTypedLocation();
  const returnsFlowLocation =
    typedLocation.state?.returnsFlowLocation ?? "departing";
  const originSearch = useSearchInput(
    {
      id: "origin",
      place: origin,
      onSelect: handleOriginAutocompleteSelect,
    },
    keyboard,
  );

  const destinationSearch = useSearchInput(
    {
      id: "destination",
      place: destination,
      onSelect: handleDestinationAutocompleteSelect,
    },
    keyboard,
  );

  function handleSwitch() {
    sendAnalyticsInteractionEvent({
      category: "SearchBox",
      action: "Click:Switch",
    });

    if (origin?.canonicalName === destination?.canonicalName) return;

    destinationSearch.autocompleteState.changeDisplayValueWithoutResults(
      originSearch.autocompleteState.displayValue,
    );

    originSearch.autocompleteState.changeDisplayValueWithoutResults(
      destinationSearch.autocompleteState.displayValue,
    );

    originSearch.setIsResultsClosed(true);
    destinationSearch.setIsResultsClosed(true);
    originSearch.setSelectedPlace(destinationSearch.selectedPlace);
    destinationSearch.setSelectedPlace(originSearch.selectedPlace);
  }

  function handleOriginAutocompleteSelect(place: AutocompletePlace) {
    sendAnalyticsInteractionEvent({
      category: "Autocomplete",
      action: "Click:AutocompleteOption",
    });
  }

  function handleDestinationAutocompleteSelect(place: AutocompletePlace) {
    sendAnalyticsInteractionEvent({
      category: "Autocomplete",
      action: "Click:AutocompleteOption",
    });
  }

  function onFocusChanged(newFocusedElement: FocusedElement) {
    const numResults =
      newFocusedElement.id === "origin"
        ? originSearch.displayResults.length
        : destinationSearch.displayResults.length;
    keyboard.setFocusedElement(newFocusedElement, numResults);
  }

  const originLabel = intl.formatMessage(inputMessages.originLabel);
  const originPlaceholder = intl.formatMessage(inputMessages.originPlaceholder);
  const destinationLabel = intl.formatMessage(inputMessages.destinationLabel);
  const destinationPlaceholder = intl.formatMessage(
    inputMessages.destinationPlaceholder,
  );

  const { dateObject } = useTravelSearchSettings();
  const [searchParams] = useSearchParams();
  const { passengerDetails, setPassengerDetails } =
    usePassengerDetailsContext();
  const { setReturnFlowState } = useReturnFlowState();

  const [travelSettings, setTravelSettings] = useState<TravelSettingsState>(
    () => {
      return {
        passengerDetails: passengerDetails,
        departureDateString: dateObject.departureDate,
        returnDateString: dateObject.returnDate,
      };
    },
  );

  const initialDateTime = formatIsoDateTime(getDefaultDateTime());
  const departureDate = !!travelSettings.departureDateString
    ? travelSettings.departureDateString
    : dateFromIsoDateTime(initialDateTime);
  const departureTime = timeFromIsoDateTime(initialDateTime);
  const fullDateTime = `${departureDate}T${departureTime}`;

  function handleDatesChange(departureDate: Date, returnDate?: Date) {
    const departureDateString = formatIsoDateTime(departureDate);
    sendAnalyticsInteractionEvent({
      category: "Search",
      action: "DatesChanged",
      label: `Departure:${departureDateString},Return:${returnDate ? formatIsoDateTime(returnDate) : "None"}`,
    });
    setTravelSettings((prev) => ({
      ...prev,
      departureDateString: formatIsoDate(departureDate),
      returnDateString: returnDate ? formatIsoDate(returnDate) : undefined,
    }));
  }

  function handleSearch() {
    let newState = typedLocation.state ?? {};

    setPassengerDetails(travelSettings.passengerDetails);

    // Handle extra return state stuff
    setReturnFlowState({
      isReturn: travelSettings.returnDateString !== undefined,
    });

    // Reset the user back to the start of the schedule selection flow when the dates change
    if (returnsFlowLocation !== "departing") {
      newState = { returnsFlowLocation: "departing" };
    }

    sendAnalyticsInteractionEvent({
      category: "Search",
      action: "Click:SearchButton",
      label: `Origin:${originSearch.selectedPlace?.canonicalName},Destination:${destinationSearch.selectedPlace?.canonicalName},Departure:${travelSettings.departureDateString},Return:${travelSettings.returnDateString ? travelSettings.returnDateString : "None"}`,
    });

    const isNewSearch =
      origin?.canonicalName !== originSearch.selectedPlace?.canonicalName ||
      destination?.canonicalName !==
        destinationSearch.selectedPlace?.canonicalName;

    let searchString = encodeSearchParamsForUrl(searchParams);
    if (travelSettings.departureDateString) {
      searchString = getDateParametersString(
        {
          departureDate: parseIsoDate(travelSettings.departureDateString),
          returnDate: travelSettings.returnDateString
            ? parseIsoDate(travelSettings.returnDateString)
            : undefined,
        },
        searchParams,
      );
    }

    navigate(
      {
        pathname: isNewSearch
          ? getPath(
              originSearch.selectedPlace?.canonicalName,
              destinationSearch.selectedPlace?.canonicalName,
              localeToLanguageCode(intl.locale),
            )
          : typedLocation.pathname,
        hash: isNewSearch ? undefined : typedLocation.hash,
        search: searchString,
      },
      {
        state: newState,
      },
    );
  }

  const hasSearchChanged =
    origin?.canonicalName !== originSearch.selectedPlace?.canonicalName ||
    destination?.canonicalName !==
      destinationSearch.selectedPlace?.canonicalName ||
    travelSettings.departureDateString !== dateObject.departureDate ||
    (travelSettings.returnDateString ?? "") !== dateObject.returnDate ||
    travelSettings.passengerDetails !== passengerDetails;

  return (
    <FocusContext
      value={{ focusedElement: keyboard.focusedElement, onFocusChanged }}
    >
      <div className="relative flex items-center rounded-full bg-neutral-700">
        <SearchInput
          id="origin"
          label={intl.formatMessage(inputMessages.originLabel)}
          className="flex-wrap"
          search={originSearch}
          keyboard={keyboard}
          aria-label={originLabel}
          placeholder={originPlaceholder}
          afterSelectEnter={() => destinationSearch.inputRef.current?.focus()}
        />
        <Button
          className="box-border flex h-8 w-8 cursor-pointer items-center justify-center rounded-full hover:bg-neutral-800 focus:outline-1 focus:outline-ring"
          onPress={handleSwitch}
          aria-label={intl.formatMessage(messages.switchLabel)}
        >
          <SwapHorizontal title="switch" className="h-auto w-6" />
        </Button>
        <SearchInput
          label={intl.formatMessage(inputMessages.destinationLabel)}
          id="destination"
          className="flex-wrap"
          search={destinationSearch}
          keyboard={keyboard}
          aria-label={destinationLabel}
          placeholder={destinationPlaceholder}
          afterSelectEnter={() => {
            searchButtonRef.current?.focus();
            searchButtonRef.current?.setAttribute("data-focus-visible", "true");
          }}
        />
        <SearchCombinedPicker
          beginDate={localDateObj(fullDateTime)}
          endDate={
            travelSettings.returnDateString
              ? localDateObj(travelSettings.returnDateString)
              : undefined
          }
          beginLabel={intl.formatMessage(messages.departureLabel)}
          endLabel={intl.formatMessage(messages.returnLabel)}
          promptBeginText={intl.formatMessage(messages.promptBeginText)}
          promptEndText={intl.formatMessage(messages.promptEndText)}
          onDatesChange={handleDatesChange}
          onSelectEndDate={(newReturnDate) => {
            if (!newReturnDate) return;
            handleDatesChange(localDateObj(fullDateTime), newReturnDate);
          }}
          passengerDetails={travelSettings.passengerDetails}
          onPassengerChange={(newPassengerDetails) => {
            setTravelSettings((prev) => ({
              ...prev,
              passengerDetails: newPassengerDetails,
            }));
          }}
          onBeginDateToggle={(isOpening) => {
            sendAnalyticsInteractionEvent({
              category: "TopNav",
              action: `DeparturePicker${isOpening ? "Opened" : "Closed"}`,
              label: `Departure:${fullDateTime}`,
            });
          }}
          onEndDateToggle={(isOpening) => {
            sendAnalyticsInteractionEvent({
              category: "TopNav",
              action: `ReturnPicker${isOpening ? "Opened" : "Closed"}`,
              label: `Return:${dateObject.returnDate || "None"}`,
            });
          }}
          onPassengerToggle={(isOpening) => {
            sendAnalyticsInteractionEvent({
              category: "TopNav",
              action: `PassengerPicker${isOpening ? "Opened" : "Closed"}`,
            });
          }}
        />
        <SearchButton
          onPress={handleSearch}
          ref={searchButtonRef}
          isDisabled={!hasSearchChanged}
        />
      </div>
    </FocusContext>
  );
}
